import { AdresseGouv } from '@mairistem/adresses';
import { Button, Form, Grid, Input, InputMask, Tooltip } from '@mairistem/composants';
import _ from 'lodash';
import React from 'react';

import type { TresorerieType } from '@/types';
import PosteComptableAdresseList from '../PosteComptableAdresseList/PosteComptableAdresseList';
import PosteComptableContactList from '../PosteComptableContactList/PosteComptableContactList';

type PosteComptableFormProps = {
  modifiable?: boolean;
  vertical?: boolean;
  tresorerie?: Omit<TresorerieType, 'identifiant'>;
  errors?: Record<string, string>;
  onChange?: (tresorerie: Partial<TresorerieType>) => void;
  onEditClick?: () => void;
};

const PosteComptableForm = (props: PosteComptableFormProps) => {
  const { modifiable = false, vertical = false, tresorerie, errors, onChange, onEditClick } = props;

  const { code, nom, telephone, mail, adresse } = tresorerie || {};

  const handleChange = React.useCallback(
    (e, { name, value }) => {
      if (onChange) {
        onChange({
          ...tresorerie,
          [name]: value,
        });
      }
    },
    [onChange, tresorerie],
  );

  const handleChangeCodeTresorerie = React.useCallback(
    (e, { value }) => {
      // On autorise que les caractères alphanumériques
      if (!/^[a-zA-Z0-9]*$/.test(value)) {
        return;
      }

      if (onChange) {
        onChange({
          ...tresorerie,
          code: _.toUpper(value),
        });
      }
    },
    [onChange, tresorerie],
  );

  const handleAdresseChange = React.useCallback(
    (adr) => {
      if (onChange) {
        onChange({
          ...tresorerie,
          // @ts-expect-error
          adresse: {
            ..._.omit(adr, [
              // on omit l'identifiant, ce sera une nouvelle adresse quoi qu'il arrive
              'identifiant',
              'codePostal',
              'commune',
              'indiceRepetition',
              'pays',
              'voie',
            ]),
            numeroCodePostal: adr?.codePostal,
            localite: adr?.commune,
            lettre: adr?.indiceRepetition,
            libellePays: adr?.pays,
            libelleVoie: adr?.voie,
            // 5 caractères max en bd
            complementNumeroVoirie: adr?.complementNumeroVoirie?.substring(0, 5),
          },
        });
      }
    },
    [onChange, tresorerie],
  );

  if (vertical) {
    return (
      <Grid as={Form}>
        <Grid.Row>
          <Grid.Column width={10}>
            <Form.Input
              error={_.has(errors, 'nom')}
              input={{
                'data-testid': 'poste-comptable-input-nom',
              }}
              required={modifiable}
              label="Nom du poste comptable"
              name="nom"
              value={nom || ''}
              onChange={handleChange}
              disabled={!modifiable}
            />
          </Grid.Column>
          <Grid.Column width={6}>
            {!_.isNil(tresorerie) && (
              <Form.Input
                error={_.has(errors, 'code')}
                input={{
                  'data-testid': 'poste-comptable-input-code',
                }}
                required={modifiable}
                label="Identifiant du poste comptable"
                name="code"
                value={code || ''}
                maxLength="6"
                onChange={handleChangeCodeTresorerie}
                style={{ width: 100 }}
                disabled={!modifiable}
              />
            )}
          </Grid.Column>
        </Grid.Row>
        {!_.isNil(tresorerie) && (
          <>
            <Grid.Row>
              <Grid.Column>
                {modifiable ? (
                  <Form.Field required error={_.has(errors, 'adresse')}>
                    <label>Adresse</label>
                    <AdresseGouv
                      error={_.has(errors, 'adresse')}
                      adresse={{
                        ...adresse,
                        codePostal: adresse?.numeroCodePostal,
                        commune: adresse?.localite,
                        indiceRepetition: adresse?.lettre,
                        pays: adresse?.libellePays,
                        voie: adresse?.libelleVoie,
                      }}
                      onAdresseChange={handleAdresseChange}
                    />
                  </Form.Field>
                ) : (
                  <PosteComptableAdresseList adresse={adresse} disabled />
                )}
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column>
                {modifiable ? (
                  <Form.Group>
                    <Tooltip
                      trigger={
                        <Form.Field>
                          <label>Téléphone</label>
                          <InputMask
                            data-testid="form-input-telephone"
                            // @ts-expect-error
                            name="telephone"
                            mask="99 99 99 99 99"
                            value={telephone || ''}
                            icon={{ name: 'bell' }}
                            onChange={(e) =>
                              handleChange(null, { name: 'telephone', value: e.target.value })
                            }
                            style={{
                              width: 175,
                              // Pour une raison que j'ignore, le Field veut pas se mettre en erreur..
                              // Donc rustine le temps qu'on ait un champs de téléphone FW
                              ...(_.has(errors, 'telephone') && { borderColor: '#96222a' }),
                            }}
                          />
                        </Form.Field>
                      }
                      type={errors?.telephone ? 'error' : ''}
                      message={errors?.telephone}
                    />
                    <Form.Field width={16} error={_.has(errors, 'mail')}>
                      <label>Courriel</label>
                      <Input
                        error={_.has(errors, 'mail')}
                        data-testid="form-input-mail"
                        type="email"
                        icon="mail"
                        name="mail"
                        value={mail}
                        onChange={handleChange}
                      />
                    </Form.Field>
                  </Form.Group>
                ) : (
                  <PosteComptableContactList
                    telephone={telephone}
                    mail={mail}
                    disabled={!modifiable}
                  />
                )}
              </Grid.Column>
            </Grid.Row>
          </>
        )}
      </Grid>
    );
  }

  return (
    <Grid as={Form} stackable>
      <Grid.Row>
        <Grid.Column width={6}>
          <Form.Group>
            <Form.Field
              required={modifiable}
              disabled={!modifiable}
              error={_.has(errors, 'nom')}
              width={_.isEmpty(tresorerie) ? 16 : 15}
            >
              <label>Nom du poste comptable</label>
              <Input
                input={{ 'data-testid': 'poste-comptable-input-nom' }}
                name="nom"
                value={nom || ''}
                onChange={handleChange}
              />
            </Form.Field>
            {onEditClick && (
              <Form.Field>
                <label>&nbsp;</label>
                <Button
                  title="Modifier ce poste comptable"
                  basic
                  fluid
                  icon="pen"
                  onClick={onEditClick}
                />
              </Form.Field>
            )}
          </Form.Group>
        </Grid.Column>
        <Grid.Column width={1} />
        <Grid.Column width={7}>
          {!_.isNil(tresorerie) && (
            <Form.Input
              input={{
                'data-testid': 'poste-comptable-input-code',
              }}
              required={modifiable}
              label="Identifiant du poste comptable"
              name="code"
              value={code || ''}
              maxLength="6"
              onChange={handleChangeCodeTresorerie}
              style={{ width: 100 }}
              disabled={!modifiable}
            />
          )}
        </Grid.Column>
      </Grid.Row>
      {!_.isNil(tresorerie) && (
        <Grid.Row style={{ paddingBottom: 0 }}>
          <Grid.Column width={6}>
            {modifiable ? (
              <Form.Field required>
                <label>Adresse</label>
                <AdresseGouv
                  adresse={{
                    ...adresse,
                    codePostal: adresse?.numeroCodePostal,
                    commune: adresse?.localite,
                    indiceRepetition: adresse?.lettre,
                    pays: adresse?.libellePays,
                    voie: adresse?.libelleVoie,
                  }}
                  onAdresseChange={handleAdresseChange}
                />
              </Form.Field>
            ) : (
              <PosteComptableAdresseList adresse={adresse} disabled />
            )}
          </Grid.Column>
          <Grid.Column width={1} />
          <Grid.Column width={6}>
            {(modifiable || !_.isEmpty(telephone) || !_.isEmpty(mail)) && (
              <PosteComptableContactList telephone={telephone} mail={mail} disabled={!modifiable} />
            )}
          </Grid.Column>
          <Grid.Column width={3} />
        </Grid.Row>
      )}
    </Grid>
  );
};

export default PosteComptableForm;
